import { AlertMessage, MUIIcon, ProgressIndicator } from "../../../widgets"
import { ButtonStyle, ControlSize, ControlType, PageMode, Placement, Property, PropertyType } from "../../../base/types"
import { FC, useEffect, useState } from "react"
import { IControlAttributes, IPageLink } from "../../../base/models"

import { DefaultLabel } from "../../widgets"
import { DesignerMode } from "../../types"
import { IPageView } from "../../page/models"
import Offcanvas from 'react-bootstrap/Offcanvas';
import { PageViewer } from "../../page/page/PageViewer"
import { Registry } from "../../../base/register"
import clsx from "clsx"
import { getButtonClass } from "../buttons/helper"
import { useGetProperties } from "../../../base/hooks"
import { useLazyGetPageMetadataQuery } from "../../../services/data/pagesMetadataApi"

export const OffcanvasComponent: FC<IControlAttributes> = ({
    control, className, children, props, designerMode, ...rest
}) => {
    const { properties } = useGetProperties({ bindings: control.bindings });
    const label: string | undefined = properties[Property.Label];
    const icon: string | undefined = properties[Property.MaterialIcon]
    const style: ButtonStyle | undefined = properties[Property.BackgroundStyle]
    const size: ControlSize | undefined = properties[Property.Size]
    const tooltip: string | undefined = properties[Property.Tooltip]
    const heading: string | undefined = properties[Property.Heading]
    const placement: Placement | undefined = properties[Property.Placement]
    const pageLink: IPageLink | undefined = properties[Property.PageLink]

    const [showDialog, setShowDialog] = useState<boolean>(false)

    if (designerMode === DesignerMode.Draft) {
        return (
            <div className={clsx(getButtonClass(style, size, "btn btn-link btn-sm"), className)} title={tooltip}>
                {(icon) && <MUIIcon iconName={icon} className={clsx({ "me-1": !!label })} />}
                <DefaultLabel
                    control={control}
                    propertyId={Property.Label}
                    defaultLabel="Offcanvas"
                    className="text-hover-primary" />
                {children}
            </div>)
    }

    const showOffCanvas = () => {
        setShowDialog(true)
    }

    return (
        <>
            <button
                type="button"
                className={clsx(getButtonClass(style, size, "btn btn-link btn-sm"), className)}
                onClick={showOffCanvas}>
                {(icon) && <MUIIcon iconName={icon} className={clsx({ "me-1": !!label })} />}
                {label && <span>{label}</span>}
                {children}
            </button>

            <OffCanvasModel
                heading={heading}
                placement={placement}
                pageLink={pageLink}
                show={showDialog}
                hideModal={() => setShowDialog(false)} />
        </>)
}

export const OffCanvasModel = ({ show, heading, placement, pageLink, hideModal }: {
    show: boolean,
    heading?: string,
    placement?: Placement,
    pageLink?: IPageLink,
    hideModal: () => void
}) => {
    const [getPageMetadata, { data: page, isLoading, isSuccess, isError, error }] = useLazyGetPageMetadataQuery();

    useEffect(() => {
        if (!pageLink?.pageId) return;
        getPageMetadata(pageLink.pageId, true);
        // eslint-disable-next-line
    }, [pageLink?.pageId])

    const getPlacement = () => {
        switch (placement) {
            case Placement.Start: return "start";
            case Placement.End: return "end";
            case Placement.Top: return "top";
            case Placement.Bottom: return "bottom";
            default: return "start"
        }
    }

    const pageView: IPageView = {
        systemId: pageLink?.systemId,
        pageMode: pageLink?.pageMode || PageMode.View,
        linkFilters: pageLink?.filters
    }

    return <Offcanvas show={show} placement={getPlacement()} onHide={hideModal}>
        <Offcanvas.Header closeButton className="border-0 py-3">
            <Offcanvas.Title className="my-0">{heading}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
            <ProgressIndicator show={isLoading} message="Please wait..." />
            <AlertMessage
                show={isError}
                type="Error"
                message={(error as any)?.data?.error?.message || `Failed to load the page`} />
            {(isSuccess && page && pageLink) && <PageViewer page={page} pageView={pageView} showTitle={false} showRefresh={false} />}
        </Offcanvas.Body>
    </Offcanvas>
}

Registry.addControl({
    id: "23443928-f3db-4456-9f24-b37193fc7345",
    name: "Offcanvas",
    type: ControlType.Offcanvas,
    properties: [
        { id: Property.Heading, name: 'Heading', type: PropertyType.String },
        { id: Property.Placement, name: 'Placement', type: PropertyType.Placement },
        { id: Property.Label, name: 'Button Label', type: PropertyType.String },
        { id: Property.MaterialIcon, name: 'Button Icon', type: PropertyType.MaterialIcon },
        { id: Property.BackgroundStyle, name: 'Button Style', type: PropertyType.ButtonStyle },
        { id: Property.Size, name: 'Button Size', type: PropertyType.ControlSize },
        { id: Property.Tooltip, name: 'Tooltip', type: PropertyType.String },
        { id: Property.PageLink, name: 'Page Link', type: PropertyType.PageLink },
    ],
    component: OffcanvasComponent
})
